import * as React from 'react';
import { SmilePlus } from 'lucide-react';
import { useApiQuery, useApiMutation, useQueryClient } from '@tyndo/hooks';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';

/**
 * Per-emoji reaction roll-up for one comment, as returned by
 * `GET /comments/{id}/reactions` (the backend's `ReactionSummary`).
 */
interface ReactionSummary {
  emoji: string;
  count: number;
  reactorUserIds: string[];
}

interface ReactionsResponse {
  entries: ReactionSummary[];
  total: number;
}

interface ToggleResponse {
  commentId: string;
  emoji: string;
  applied: boolean;
}

export interface CommentReactionsProps {
  /** Id of the (entity) comment the reactions hang off. */
  commentId: string;
  /**
   * The current operator's user id. Used to highlight emoji the caller has
   * already reacted with so the toggle reads as a pressed/unpressed state.
   * Optional — when absent, reactions still render and toggle, they just
   * aren't visually marked as "mine".
   */
  currentUserId?: string;
  className?: string;
}

/**
 * The quick-pick palette offered behind the "add reaction" affordance.
 * Mirrors the Slack/Teams baseline set; the backend accepts any token, so
 * this is purely a convenience picker, not a closed enum.
 */
const QUICK_EMOJI = ['👍', '👎', '✅', '🎉', '👀', '❤️', '🚀', '🙏'] as const;

/**
 * Emoji-reaction strip for a comment (the Slack/Teams "react" affordance).
 * Self-contained: it fetches the comment's reaction roll-up and toggles a
 * reaction through the polymorphic `/comments/{id}/reactions` endpoints, so a
 * consuming view only has to drop `<CommentReactions commentId={…} />` next to
 * a comment — no per-view API wiring. Reaction state is invalidated on toggle
 * so the roll-up reflects the new count immediately.
 */
export function CommentReactions({
  commentId,
  currentUserId,
  className,
}: CommentReactionsProps) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const endpoint = `/comments/${commentId}/reactions`;

  const reactionsQ = useApiQuery<ReactionsResponse>(endpoint, { retry: false });
  const toggleMut = useApiMutation<ToggleResponse, { emoji: string }>(endpoint, 'POST');

  const summaries = React.useMemo<ReactionSummary[]>(
    () =>
      Array.isArray(reactionsQ.data?.entries)
        ? reactionsQ.data.entries.filter((r): r is ReactionSummary => r != null)
        : [],
    [reactionsQ.data],
  );

  const toggle = React.useCallback(
    (emoji: string) => {
      setPickerOpen(false);
      toggleMut.mutate(
        { emoji },
        {
          onSettled: () => {
            void qc.invalidateQueries({ queryKey: [endpoint] });
          },
        },
      );
    },
    [toggleMut, qc, endpoint],
  );

  const mine = React.useCallback(
    (s: ReactionSummary) =>
      !!currentUserId && s.reactorUserIds.includes(currentUserId),
    [currentUserId],
  );

  return (
    <div
      className={cn('mt-1 flex flex-wrap items-center gap-1', className)}
      data-testid={`comment-reactions-${commentId}`}
    >
      {summaries.map((s) => (
        <button
          key={s.emoji}
          type="button"
          onClick={() => toggle(s.emoji)}
          disabled={toggleMut.isPending}
          aria-pressed={mine(s)}
          title={s.reactorUserIds.join(', ')}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
            mine(s)
              ? 'border-primary/60 bg-primary/10 text-primary'
              : 'border-border/60 bg-surface-overlay hover:bg-muted/50',
          )}
          data-testid={`comment-reaction-${commentId}-${s.emoji}`}
        >
          <span aria-hidden="true">{s.emoji}</span>
          <span className="tabular-nums">{s.count}</span>
        </button>
      ))}

      <div className="relative">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setPickerOpen((v) => !v)}
          disabled={toggleMut.isPending}
          aria-label="Add reaction"
          aria-expanded={pickerOpen}
          data-testid={`comment-reaction-add-${commentId}`}
        >
          <SmilePlus className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
        {pickerOpen && (
          <div
            className="absolute z-10 mt-1 flex gap-1 rounded-md border border-border/60 bg-surface p-1 shadow-md"
            role="menu"
            data-testid={`comment-reaction-picker-${commentId}`}
          >
            {QUICK_EMOJI.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => toggle(emoji)}
                className="rounded px-1.5 py-0.5 text-base hover:bg-muted/50"
                aria-label={`React with ${emoji}`}
                data-testid={`comment-reaction-pick-${commentId}-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

CommentReactions.displayName = 'CommentReactions';
